import { initializeApp, FirebaseApp, FirebaseOptions } from 'firebase/app';
import {
  getAuth,
  Auth,
  GoogleAuthProvider,
  signInAnonymously,
  signInWithPopup,
  signOut,
  setPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import { clearCurrentWorkspaceId } from '../services/axios-instance';

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;

/**
 * Detect if we're running in E2E testing mode
 */
function isE2EMode(): boolean {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const isE2EParam = urlParams.get('e2e') === 'true';
    return isE2EParam;
  }
  return false;
}

/**
 * Initialize Firebase with config from app
 * Must be called before using any Firebase auth methods
 */
export const initializeFirebase = (config: FirebaseOptions): FirebaseApp => {
  if (!firebaseApp) {
    firebaseApp = initializeApp(config);
    firebaseAuth = getAuth(firebaseApp);

    // Set persistence
    setupAuthPersistence();
  }
  return firebaseApp;
};

/**
 * Get Firebase Auth instance
 * Throws error if not initialized
 */
export const getFirebaseAuth = (): Auth => {
  if (!firebaseAuth) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return firebaseAuth;
};

// Set persistence based on environment
const setupAuthPersistence = async () => {
  if (!firebaseAuth) return;

  try {
    const persistence = isE2EMode() ? browserLocalPersistence : indexedDBLocalPersistence;
    await setPersistence(firebaseAuth, persistence);

    if (isE2EMode()) {
      console.log('🧪 E2E Mode: Firebase auth configured to use localStorage');
    }
  } catch (error) {
    console.warn('Failed to set Firebase auth persistence:', error);
  }
};

// Google Auth Provider
export const googleProvider = new GoogleAuthProvider();

// Auth methods
export const signInWithGoogle = () => {
  const auth = getFirebaseAuth();
  return signInWithPopup(auth, googleProvider);
};

export const signInAsGuest = () => {
  const auth = getFirebaseAuth();
  return signInAnonymously(auth);
};

export const logOut = async () => {
  const auth = getFirebaseAuth();
  await signOut(auth);
  // Drop the persisted workspace selection so the next user who signs in on
  // this device doesn't inherit it (and get 403 from X-Workspace-ID).
  clearCurrentWorkspaceId();
};

// Email Link (Passwordless) Authentication
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

export const sendEmailSignInLink = async (email: string, callbackUrl?: string) => {
  const auth = getFirebaseAuth();
  const url = callbackUrl || `${window.location.origin}/auth/email-callback`;

  const actionCodeSettings = {
    url,
    handleCodeInApp: true,
  };

  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  // Save email to localStorage for callback
  localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
};

export const isEmailSignInLink = (url?: string): boolean => {
  const auth = getFirebaseAuth();
  return isSignInWithEmailLink(auth, url || window.location.href);
};

export const completeEmailSignIn = async (url?: string, email?: string) => {
  const auth = getFirebaseAuth();
  const signInUrl = url || window.location.href;

  if (!isSignInWithEmailLink(auth, signInUrl)) {
    throw new Error('Invalid sign-in link');
  }

  // Get email from parameter or localStorage
  const signInEmail = email || localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
  if (!signInEmail) {
    throw new Error('Please provide your email to complete sign-in');
  }

  const result = await signInWithEmailLink(auth, signInEmail, signInUrl);
  localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
  return result;
};

export const getStoredEmailForSignIn = (): string | null => {
  return localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
};

export const clearStoredEmailForSignIn = (): void => {
  localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
};
